/* eslint-disable no-shadow */
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { HttpError, PMSError, ValidationError } from '@/errors';
import Vue from 'vue';

const endpoint = `${apiEndpoint}/webhook/subscription`;

const state = {
  subscriptions: null,
  eventTypes: null,
  notifications: null,
  pending: false,
  error: null,
  updateError: null,
  subscriptionsPending: false,
  notificationsPending: false,
};

const getters = {
  loaded: (state) => state.subscriptions != null && state.eventTypes != null && state.notifications != null,
  subscriptions: (state) => state.subscriptions || [],
  eventTypes: (state) => state.eventTypes || [],
  notifications: (state) => state.notifications || [],
  hasSubscriptions: (state, getters) => (getters.loaded ? state.subscriptions?.length > 0 : false),
};

const mutations = {
  clearErrors(state) {
    state.error = null;
    state.updateError = null;
  },
  beforeLoading(state, initial = false) {
    if (initial) {
      state.group = null;
    }
    state.error = null;
    state.pending = true;
    state.invalid = false;
  },
  beforeUpdate(state) {
    state.updateError = null;
    state.subscriptionsPending = true;
  },
  afterUpdate(state) {
    state.subscriptionsPending = false;
  },
  data(state, { data = null, error = null, update = false } = {}) {
    if (!update) {
      state.pending = false;
    } else {
      state.subscriptionsPending = false;
    }

    state.subscriptions = data;
    state.error = error;
  },

  eventTypes(state, eventTypes) {
    state.eventTypes = eventTypes;
  },

  notifications(state, data) {
    state.notifications = data;
  },
  beforeSubscriptionsRequest(state) {
    state.subscriptionsPending = true;
  },
  beforeNotificationRequest(state) {
    state.notificationsPending = true;
  },
  afterSubscriptionsRequest(state) {
    state.subscriptionsPending = false;
  },
  afterNotificationRequest(state) {
    state.notificationsPending = false;
  },
  addSubscription(state, subscription) {
    if (state.subscriptions == null) {
      state.subscriptions = [];
    }
    state.subscriptions.unshift(subscription);
    state.subscriptionsPending = false;
  },
  updateSubscription(state, subscription) {
    const idx = state.subscriptions.findIndex(({ id }) => id === subscription.id);
    if (idx !== -1) {
      Vue.set(state.subscriptions, idx, subscription);
    }
    state.subscriptionsPending = false;
  },
  deleteSubscription(state, pId) {
    const idx = state.subscriptions.findIndex(({ id }) => Number(id) === Number(pId));
    if (idx > -1) {
      state.subscriptions.splice(idx, 1);
    }
    state.subscriptionsPending = false;
  },
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 400: // BadRequest
      err = new HttpError(status, data.message);
      break;
    case 409: // PMSError
      err = new PMSError(status, data.message);
      break;
    case 422:
      err = new ValidationError(status, data.message, data.errors);
      break;
    case 500:
      err = new Error(data.message);
      break;
    default:
      break;
  }
  // commit('update', err);
  throw err;
}

const actions = {
  async createSubscription({ commit }, payload) {
    commit('beforeSubscriptionsRequest');
    try {
      const { data } = await ApiService.post(`${endpoint}`, payload);
      commit('addSubscription', data);
      return data;
    } catch (error) {
      commit('afterSubscriptionsRequest');
      handleApiError(commit, error);
      return null;
    }
  },
  async updateSubscription({ commit }, subscription) {
    commit('beforeSubscriptionsRequest');
    try {
      const { id } = subscription;
      const { data } = await ApiService.put(`${endpoint}/${id}`, subscription);
      commit('updateSubscription', data);
    } catch (error) {
      commit('afterSubscriptionsRequest');
      handleApiError(commit, error);
    }
  },
  async deleteSubscription({ commit }, id) {
    commit('beforeSubscriptionsRequest');
    try {
      await ApiService.delete(`${endpoint}/${id}`);
      commit('deleteSubscription', id);
    } catch (error) {
      commit('afterSubscriptionsRequest');
      throw handleApiError(commit, error);
    }
  },
  async fetchSubscriptions({ commit }, { pageNb = null, filter = '' } = {}) {
    commit('beforeSubscriptionsRequest');
    const page = pageNb || 1;

    try {
      const { data } = await ApiService.get(`${endpoint}?page=${page}&filter=${filter}`);
      commit('data', { data });
      commit('afterSubscriptionsRequest');
      return data;
    } catch (error) {
      commit('data', { error });
      throw handleApiError(commit, error);
    }
  },

  async fetchAllEvents({ commit }) {
    try {
      const { data } = await ApiService.get(`${endpoint}/get-events`);
      commit('eventTypes', data);
      return data;
    } catch (error) {
      commit('data', { error });
      throw handleApiError(commit, error);
    }
  },

  async fetchNotifications({ commit }, { subscription = '', from = '', until = '' }) {
    commit('beforeNotificationRequest');
    try {
      const { data } = await ApiService.get(
        `${endpoint}/notifications?subscription=${subscription}&from=${from}&until=${until}`,
      );
      commit('notifications', data);
      commit('afterNotificationRequest');
      return data;
    } catch (error) {
      commit('data', { error });
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
